import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { FaRedo, FaCheckCircle } from 'react-icons/fa';
import './Captcha.css';

const CHARSET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const LENGTH = 5;
const WIDTH = 260;
const HEIGHT = 72;

const generateCode = () => {
  let s = '';
  for (let i = 0; i < LENGTH; i++) {
    s += CHARSET[Math.floor(Math.random() * CHARSET.length)];
  }
  return s;
};

const draw = (canvas, code) => {
  const ctx = canvas.getContext('2d');
  const dpr = window.devicePixelRatio || 1;
  canvas.width = WIDTH * dpr;
  canvas.height = HEIGHT * dpr;
  canvas.style.width = WIDTH + 'px';
  canvas.style.height = HEIGHT + 'px';
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, WIDTH, HEIGHT);

  const bg = ctx.createLinearGradient(0, 0, WIDTH, HEIGHT);
  bg.addColorStop(0, '#eef2ff');
  bg.addColorStop(0.5, '#e0e7ff');
  bg.addColorStop(1, '#dbeafe');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, WIDTH, HEIGHT);

  for (let i = 0; i < 4; i++) {
    ctx.strokeStyle = `rgba(62, 106, 246, ${0.12 + Math.random() * 0.18})`;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(Math.random() * WIDTH, Math.random() * HEIGHT);
    ctx.bezierCurveTo(
      Math.random() * WIDTH, Math.random() * HEIGHT,
      Math.random() * WIDTH, Math.random() * HEIGHT,
      Math.random() * WIDTH, Math.random() * HEIGHT
    );
    ctx.stroke();
  }

  for (let i = 0; i < 50; i++) {
    ctx.fillStyle = `rgba(30, 41, 59, ${Math.random() * 0.25})`;
    ctx.fillRect(Math.random() * WIDTH, Math.random() * HEIGHT, 2, 2);
  }

  const spacing = WIDTH / (code.length + 1);
  for (let i = 0; i < code.length; i++) {
    const x = spacing * (i + 1);
    const y = HEIGHT / 2 + (Math.random() - 0.5) * 12;
    const rotation = (Math.random() - 0.5) * 0.55;
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(rotation);
    ctx.font = `700 ${32 + Math.floor(Math.random() * 6)}px "Inter", system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const hue = 215 + Math.floor(Math.random() * 30);
    ctx.fillStyle = `hsl(${hue}, 75%, 32%)`;
    ctx.fillText(code[i], 0, 0);
    ctx.restore();
  }
};

const Captcha = forwardRef(({ label = 'CAPTCHA', placeholder = 'Enter the code', error }, ref) => {
  const canvasRef = useRef(null);
  const [code, setCode] = useState(generateCode);
  const [input, setInput] = useState('');

  const refresh = useCallback(() => {
    setCode(generateCode());
    setInput('');
  }, []);

  useEffect(() => {
    if (canvasRef.current) draw(canvasRef.current, code);
  }, [code]);

  useImperativeHandle(ref, () => ({
    verify: () => input.trim().toUpperCase() === code,
    refresh
  }), [input, code, refresh]);

  const isValid = input.trim().toUpperCase() === code;
  const hasInput = input.length > 0;

  return (
    <div className="captcha">
      <label htmlFor="captcha-input" className="captcha-label">
        {label} <span className="required">*</span>
      </label>

      <div className="captcha-canvas-wrap">
        <canvas
          ref={canvasRef}
          className="captcha-canvas"
          aria-label="CAPTCHA verification image"
        />
        <button
          type="button"
          className="captcha-refresh"
          onClick={refresh}
          aria-label="Refresh CAPTCHA"
          title="Refresh code"
        >
          <FaRedo />
        </button>
      </div>

      <div className={`captcha-input-wrap${isValid ? ' is-valid' : ''}${error ? ' has-error' : ''}`}>
        <input
          id="captcha-input"
          type="text"
          className="captcha-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={placeholder}
          autoComplete="off"
          spellCheck="false"
          autoCapitalize="characters"
          maxLength={LENGTH}
          required
        />
        {hasInput && isValid && (
          <FaCheckCircle className="captcha-check" aria-hidden="true" />
        )}
      </div>
    </div>
  );
});

Captcha.displayName = 'Captcha';

export default Captcha;
